<script setup lang="ts">
import { z } from 'zod';
import { Plus, X } from 'lucide-vue-next';
import { breakpointsTailwind } from '@vueuse/core';

const prefix = 'components.search.multidestForm';

const emit = defineEmits(['search']);

const searchCriteriaStore = useSearchCriteriaStore();
const route = useRoute();
// Route name, not path: with the prefix_and_default strategy the default locale has both /results and /fr/results.
const routeBaseName = useRouteBaseName();
const legLabelId = useId();
const breakpoints = useBreakpoints(breakpointsTailwind);
// smaller, not smallerOrEqual: the mobile trigger is md:hidden, which already hides it at 768px.
const isMobile = breakpoints.smaller('md');
const addFlightButton = useTemplateRef('addFlightButton');
const { focused: addFlightButtonFocused } = useFocus(addFlightButton);

const submitted = ref(false);

const errorsPrefix = 'components.search.form.errors';
const legSchema = z
  .object({
    from: z.string().min(1, `${errorsPrefix}.origin`),
    to: z.string().min(1, `${errorsPrefix}.destination`),
    date: z
      .string()
      .min(1, `${errorsPrefix}.departureDate`)
      .refine(isSearchDepartureDateAllowed, `${errorsPrefix}.departureDateTooSoon`)
      .refine(isSearchDepartureDateNotTooFar, `${errorsPrefix}.departureDateTooFar`),
  })
  .refine((data) => data.from !== data.to, {
    message: `${errorsPrefix}.sameCity`,
    path: ['to'],
  });

const errors = computed(() =>
  searchCriteriaStore.searchRequest.legs.map((leg) => {
    const fromCode = leg.from?.code ?? '';
    const toCode = leg.to?.code ?? '';
    const departureDate = leg.departureDate;
    if (!submitted.value) return { from: undefined, to: undefined, date: undefined };
    const result = legSchema.safeParse({
      from: fromCode,
      to: toCode,
      date: departureDate ?? '',
    });
    if (result.success) return { from: undefined, to: undefined, date: undefined };
    const { fieldErrors } = result.error.flatten();
    return { from: fieldErrors.from?.[0], to: fieldErrors.to?.[0], date: fieldErrors.date?.[0] };
  }),
);

const properties = defineProps<{ loading?: boolean }>();

type LegField = 'from' | 'to' | 'date';
// A count, so asking the same field twice still changes its prop.
const forcedFocus = ref<{ index: number; field: LegField; count: number }>();

// nextTick: a just-added leg, or the errors a field announces, must be rendered first.
const forceFocus = async (index: number, field: LegField) => {
  await nextTick();
  forcedFocus.value = { index, field, count: (forcedFocus.value?.count ?? 0) + 1 };
};

const forceFocusFor = (index: number, field: LegField) =>
  forcedFocus.value?.index === index && forcedFocus.value.field === field ? forcedFocus.value.count : undefined;

// The add button may give way to the max-flights text, which would drop the focus on the body: move it to the new
// leg's first empty field instead.
const addLeg = async () => {
  searchCriteriaStore.addLeg();
  const index = searchCriteriaStore.searchRequest.legs.length - 1;
  const hasOrigin = Boolean(searchCriteriaStore.searchRequest.legs[index]?.from.code);
  await forceFocus(index, hasOrigin ? 'to' : 'from');
};

const removeLeg = async (index: number) => {
  searchCriteriaStore.removeLeg(index);
  await nextTick();
  addFlightButtonFocused.value = true;
};

const firstInvalidField = (error: { from?: string; to?: string }): LegField => {
  if (error.from) {
    return 'from';
  }
  if (error.to) {
    return 'to';
  }
  return 'date';
};

const triggerSearch = async () => {
  submitted.value = true;
  const index = errors.value.findIndex((error) => error.from || error.to || error.date);
  const error = errors.value[index];
  if (!error) {
    emit('search');
    return;
  }
  await forceFocus(index, firstInvalidField(error));
};

const destinationSheetOpenIndex = ref<number>();

const onOriginSelect = async (index: number) => {
  if (!isMobile.value || searchCriteriaStore.searchRequest.legs[index]?.to?.code) {
    return;
  }
  // Wait for the origin sheet to be really closed, otherwise the destination sheet opens on top of it.
  await nextTick();
  destinationSheetOpenIndex.value = index;
};
</script>

<template>
  <TransitionGroup
    tag="div"
    class="flex flex-col gap-4 md:gap-3"
    enter-active-class="transition-all duration-300 ease-out motion-reduce:transition-none"
    enter-from-class="opacity-0 -translate-y-2"
  >
    <!-- No comment above the root: in dev Vue keeps it and the fragment root breaks the parent's out-in Transition.
         md:gap-3: the floated labels sit on the top border and overflow the row by half their height. gap-4 below md:
         the stacked legs need a clearer break between one flight's fields and the next one's title.
         Enter only: legs are keyed by index, a leave animation would fade the last row out whichever leg is removed. -->
    <div
      v-for="(leg, index) in searchCriteriaStore.searchRequest.legs"
      :key="index"
      role="group"
      :aria-labelledby="`${legLabelId}-${index}`"
      class="grid gap-3 md:grid-cols-multidest-leg md:gap-x-2"
    >
      <!-- Mobile: title + remove. The title also names the leg's group on desktop: aria-labelledby reads a hidden
           element, so the six "From" fields each come with their flight number. -->
      <div class="flex items-center justify-between md:hidden">
        <span
          :id="`${legLabelId}-${index}`"
          class="text-sm font-semibold text-text-primary-900"
          >{{ $t(`${prefix}.flight`) }} {{ index + 1 }}</span
        >
        <UiButton
          v-if="index >= 2"
          variant="tertiary"
          color="gray"
          size="sm"
          padding="none"
          :aria-label="$t(`${prefix}.removeFlight`)"
          @click="removeLeg(index)"
        >
          {{ $t(`${prefix}.remove`) }}
        </UiButton>
      </div>

      <SearchAutocompleteInput
        v-model="leg.from"
        :label="$t(`${prefix}.origin`)"
        :error="errors[index]?.from ? $t(errors[index].from) : undefined"
        :force-focus="forceFocusFor(index, 'from')"
        field="from"
        :leg-index="index"
        label-mode="floating"
        @select="onOriginSelect(index)"
      />

      <SearchAutocompleteInput
        :model-value="leg.to"
        :label="$t(`${prefix}.destination`)"
        :error="errors[index]?.to ? $t(errors[index].to) : undefined"
        :force-focus="forceFocusFor(index, 'to')"
        :sheet-open="destinationSheetOpenIndex === index"
        field="to"
        :leg-index="index"
        label-mode="floating"
        @update:model-value="searchCriteriaStore.setLegDestination(index, $event)"
        @update:sheet-open="destinationSheetOpenIndex = $event ? index : undefined"
      />

      <SearchDatepicker
        :departure-date="leg.departureDate"
        :departure-error="errors[index]?.date ? $t(errors[index].date) : undefined"
        :force-focus="forceFocusFor(index, 'date')"
        :min-date="searchCriteriaStore.previousFlightDate(index)"
        :one-way="true"
        label-mode="floating"
        @update:departure-date="searchCriteriaStore.setLegDepartureDate(index, $event)"
      />

      <UiButton
        v-if="index >= 2"
        variant="tertiary"
        size="icon"
        padding="none"
        class="hidden w-auto self-center md:inline-flex"
        :aria-label="$t(`${prefix}.removeFlight`)"
        @click="removeLeg(index)"
      >
        <X aria-hidden="true" />
      </UiButton>
    </div>

    <!-- Bottom row -->
    <div
      key="bottom-row"
      class="grid gap-2 @container md:grid-cols-multidest-leg"
    >
      <!-- Live region around both: a region inserted together with its text is not reliably announced. -->
      <div
        aria-live="polite"
        class="self-center justify-self-start"
      >
        <p
          v-if="searchCriteriaStore.hasMaxLegs"
          class="text-sm text-text-secondary-600"
        >
          {{ $t(`${prefix}.maxFlights`, MAX_LEGS) }}
        </p>
        <UiButton
          v-else
          ref="addFlightButton"
          variant="tertiary"
          color="brand"
          size="md"
          padding="none"
          class="[&_svg]:size-5"
          @click="addLeg()"
        >
          <Plus aria-hidden="true" />
          {{ $t(`${prefix}.addFlight`) }}
        </UiButton>
      </div>

      <div
        class="flex flex-col gap-2 border-t border-gray-300 pt-4 md:col-span-2 md:col-start-2 md:flex-row md:border-t-0 md:pt-0 md:@multidest-row:col-span-1 md:@multidest-row:col-start-3"
      >
        <div class="min-w-0 flex-1">
          <SearchPassengersTypes
            v-model:cabin-class="searchCriteriaStore.searchRequest.cabinClass"
            :adults="searchCriteriaStore.searchRequest.adults"
            :children="searchCriteriaStore.searchRequest.children"
            :infants="searchCriteriaStore.searchRequest.infants"
            @update:adults="searchCriteriaStore.updateAdults"
            @update:children="searchCriteriaStore.updateChildren"
            @update:infants="searchCriteriaStore.updateInfants"
          />
        </div>

        <UiButton
          :loading="properties.loading"
          :loading-label="$t('components.common.loading')"
          :disabled="routeBaseName(route) === 'results' && !searchCriteriaStore.hasUncommittedCriteria"
          shape="lg"
          class="h-11 w-full focus-visible:ring-brand-primary-700 md:w-auto"
          @click="triggerSearch"
        >
          <template v-if="routeBaseName(route) === 'results'">
            {{ $t(`${prefix}.actualize`) }}
          </template>
          <template v-else>
            {{ $t(`${prefix}.search`) }}
          </template>
        </UiButton>
      </div>
    </div>
  </TransitionGroup>
</template>
